import React, { useEffect, useRef, useState } from "react";
import { css, keyframes } from "@emotion/react";
import { isMobileApp } from "@sharpana/shared/utils/web_environment";
import { notifyRequestPayment, preloadPurchaseProducts } from "@sharpana/shared/utils/webViewBridge";
import { request as apiRequest } from "@sharpana/auth/api_client/allauth";
import type {
  RcOfferingsPayload,
  PaywallProducts,
  Store,
} from "../types/paywallProductPricing";
import { useSearchParams } from "react-router-dom";

const containerStyles = css`
  min-height: 100vh;
  background: #05070b;
  color: #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const mainDescription = css`
  font-size: 1.25rem;
  width: 70%;
  max-width: 850px;
  margin: 0 auto !important;
  text-align: center;
  display: block;

  @media (max-width: 768px) {
    width: 99%;
  }
`;

const headerStyles = css`
  text-align: center;
  padding: 2rem 1rem;
  width: 100%;
  position: relative;
  /* Metallic gradient that fades into the container background (#05070b) */
  background: linear-gradient(
    180deg,
    rgba(47, 63, 100, 0.45) 0%,
    /* deep steel */ rgba(14, 18, 28, 0.9) 60%,
    /* slightly darker band */ rgba(9, 12, 20, 0.85) 75%,
    /* soft transition */ rgba(5, 7, 11, 0) 100%
      /* fade to container background */
  );

  /* Subtle top sheen for a modern metallic look */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.03) 0%,
        rgba(255, 255, 255, 0.02) 10%,
        rgba(255, 255, 255, 0) 40%
      ),
      radial-gradient(
        120% 40% at 50% 0%,
        rgba(255, 255, 255, 0.06) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    pointer-events: none;
    mix-blend-mode: screen; /* subtle shine without overpowering */
  }

  h3 {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: #f8fafc;
    letter-spacing: -0.025em;
  }

  p {
    font-size: 1.125rem;
    color: rgb(206, 211, 216);
    margin: 0;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 1.875rem;
    }
  }
`;

const highlight = css`
  background: linear-gradient(90deg, #60a5fa 0%, #8b5cf6 75%, #93c5fd 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  // text-shadow: 0 0 6px rgba(139, 92, 246, 0.25), 0 1px 1px rgba(0, 0, 0, 0.35);
`;
const glowUnderline = css`
  display: inline-block;
  position: relative;
  padding-bottom: 2px;
  // text-shadow: 0 0 2px rgba(139, 92, 246, 0.25);

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(96, 165, 250, 0) 0%,
      rgba(139, 92, 246, 0.8) 50%,
      rgba(6, 182, 212, 0) 100%
    );
    opacity: 0.9;
  }
`;

const taglineStyles = css`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.6;
  margin: 1rem 0 !important;

  .minimize {
    // color: #d97706;
    color: #ce6e00;
    font-weight: 600;
    text-shadow: 0 0 3px rgba(217, 119, 6, 0.15);
    transition: all 0.3s ease;

    &:hover {
      text-shadow: 0 0 8px rgba(217, 119, 6, 0.3);
    }
  }

  .amplify {
    background: linear-gradient(135deg, #60a5fa 0%, #8b5cf6 50%, #06b6d4 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    text-shadow: 0 0 2px rgba(139, 92, 246, 0.3);
    transition: all 0.3s ease;

    &:hover {
      filter: brightness(1.2);
    }
  }

  .faster {
    position: relative;
    /* Keep word visually identical to parent gradient */
    background: inherit;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: inherit;
    text-shadow: inherit;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        #8b5cf6 50%,
        transparent 100%
      );
      opacity: 1;
      transition: opacity 0.3s ease;
    }

    &:hover::after {
      opacity: 1;
    }
  }

  .sharpen {
    color: #10b981;
    font-weight: 600;
    text-shadow: 0 0 1px rgba(16, 185, 129, 0.3);
    position: relative;
    transition: all 0.3s ease;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        #10b981 50%,
        transparent 100%
      );
      opacity: 0.6;
      transition: opacity 0.3s ease;
    }

    &:hover {
      text-shadow: 0 0 12px rgba(16, 185, 129, 0.5);

      &::after {
        opacity: 1;
      }
    }
  }

  @media (max-width: 768px) {
    font-size: 1.125rem;
    line-height: 1.5;
  }
`;

const productsContainerStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 0.5rem;
  max-width: 800px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const productCardStyles = css`
  background: #050505;
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 2rem;
  margin-left: 1rem;
  margin-right: 1rem;
  position: relative;
  /* Subtle floating shadow for dark theme */
  box-shadow: 0 28px 50px -12px rgba(0, 0, 0, 0.8),
    0 16px 28px -12px rgba(0, 0, 0, 0.55), 0 0 24px 0 rgba(139, 92, 246, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    border-color: #475569;
    /* Slight lift on hover to enhance the floating effect */
    box-shadow: 0 40px 70px -20px rgba(0, 0, 0, 0.9),
      0 20px 36px -16px rgba(0, 0, 0, 0.65), 0 0 32px 0 rgba(139, 92, 246, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const popularBadgeStyles = css`
  position: absolute;
  top: -10px;
  right: 1.5rem;
  background: #3b82f6;
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const bottomRowNotes = css`
  margin-top: 3rem;
  padding-left: 1rem;
  padding-right: 1rem;
  text-align: center;
  color: #94a3b8;
  font-size: 1rem;
  line-height: 1.2;
  max-width: 800px;
`;

const legalLinksStyles = css`
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  color: #94a3b8;
  font-size: 0.85rem;
  text-align: center;

  a {
    color: #cbd5e1;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
    color: #e2e8f0;
  }
  .dot {
    margin: 0 8px;
    color: #64748b;
  }
`;

const productHeaderStyles = css`
  text-align: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: 1.7rem;
    font-weight: 700;
    margin-bottom: 0.7rem;
    color: #f1f5f9;
  }

  .description {
    color: #94a3b8;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const priceStyles = css`
  text-align: center;
  margin-bottom: 2rem;

  .price {
    font-size: 2.5rem;
    font-weight: 700;
    color: #f8fafc;
    line-height: 1;
  }

  .currency {
    font-size: 1.25rem;
    vertical-align: top;
  }

  .usd {
    font-size: 0.65rem;
    vertical-align: super;
    margin-left: 0.25rem;
    color: #94a3b8;
  }

  .period {
    font-size: 0.875rem;
    color: #94a3b8;
    margin-left: 0.5rem;
  }

  @media (max-width: 768px) {
    .price {
      font-size: 2.25rem;
    }
  }
`;

const priceStylesShifted = css`
  ${priceStyles}
  transform: translateX(25px);
`;

const featuresStyles = css`
  margin-bottom: 2rem;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: #cbd5e1;
    line-height: 1.5;

    &::before {
      content: "✓";
      color: #10b981;
      font-weight: 500;
      margin-right: 0.75rem;
      font-size: 0.875rem;
    }
  }

  /* Heart variant for special support item */
  .heart::before {
    content: "❤️";
    color: initial;
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  // 50% { background-position: 0% 50%; }
  100% { background-position: 0% 50%; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const buttonLoading = css`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-right: 0.5rem;
`;

const buttonStyles = css`
  width: 100%;
  /* Vibrant gradient CTA — sleek, modern, and inviting */
  background: linear-gradient(
    135deg,
    rgb(102, 67, 183) 0%,
    /* violet */ rgb(72, 74, 174) 45%,
    /* indigo */ rgb(25, 157, 178) 100% /* cyan */
  );
  background-size: 200% 200%;
  background-position: left center;
  animation: ${gradientShift} 12s ease-in-out infinite;
  will-change: background-position;
  color: #f8fafc;
  border: 1px solid rgba(99, 102, 241, 0.55); /* indigo accent */
  border-radius: 10px;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.1s ease, border-color 0.2s ease;
  text-transform: none;
  letter-spacing: 0.01em;
  box-shadow: 0 2px 1px rgba(34, 211, 238, 0.14);

  &:hover {
    background-position: right center; /* animated sheen */
    animation-duration: 6s; /* slightly faster on hover */
    border-color: #7c83f5;
    transform: translateY(-1px);
    box-shadow: 0 3px 4px rgba(99, 102, 241, 0.28),
      0 3px 4px rgba(34, 211, 238, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  &:active {
    background-position: center;
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
    border-color: #60a5fa;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    /* keep gradient static to signal disabled state a bit more */
    animation-duration: 12s;
  }
`;

const subscriptionButtonStyles = css`
  ${buttonStyles}
  background: #10b981;
  border-color: #10b981;

  &:hover {
    background: #059669;
    border-color: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  }

  &:active {
    background: #047857;
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const bottomRowActions = css`
  color: rgb(154, 155, 156);
`;

// Local modal styles (scoped to this page)
const modalOverlayStyles = css`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 12, 0.65);
  backdrop-filter: saturate(120%) blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

const modalContentStyles = css`
  width: min(560px, 92vw);
  background: #0b0f17;
  border: 1px solid rgba(51, 65, 85, 0.9);
  border-radius: 12px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(148, 163, 184, 0.06) inset;
  padding: 1.25rem 1.25rem 1rem;
  color: #e2e8f0;
`;

const modalTitleStyles = css`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #f8fafc;
`;

const modalBodyStyles = css`
  color: #94a3b8;
  white-space: pre-wrap;
`;

const modalActionsStyles = css`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const modalCloseButtonStyles = css`
  background: transparent;
  color: #e5e7eb;
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #0f172a;
    border-color: #475569;
  }

  &:active {
    transform: translateY(0.5px);
  }
`;

function RestorePurchasesAction() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    isError?: boolean;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal]);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    setResult(null);
    try {
      const data: any = await apiRequest("POST", "/api/restore-purchases");
      if (!data?.success) {
        throw new Error(data?.error || "Restore failed");
      }

      const subs: string[] = Array.isArray(data.active_subscriptions_sku_names)
        ? data.active_subscriptions_sku_names
        : [];
      const balance = data?.balance;

      let msg: string;
      if (subs.length > 0) {
        msg = `Process complete, you have the following active subscriptions: ${subs.join(
          ", "
        )}`;
      } else {
        msg = `Process complete, your current credits balance is: ${balance}`;
      }

      msg +=
        "\n\n*Products are tied to your Sharpana account, if you need further help please contact us at support@mrble.ai";

      setResult({ message: msg });
      setShowModal(true);
    } catch (e) {
      setResult({
        message:
          "There was a problem restoring purchases. Please try again or contact support@mrble.ai",
        isError: true,
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <p
        css={bottomRowActions}
        onClick={loading ? undefined : handleClick}
        aria-busy={loading}
        style={loading ? { opacity: 0.7, pointerEvents: "none" } : undefined}
      >
        {loading ? (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <span css={buttonLoading} aria-hidden="true" />
            Restoring purchases…
          </span>
        ) : (
          "• Restore purchases •"
        )}
      </p>

      {result && showModal && (
        <div css={modalOverlayStyles} onClick={() => setShowModal(false)}>
          <div
            css={modalContentStyles}
            role="dialog"
            aria-modal="true"
            aria-labelledby="paywall-restore-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="paywall-restore-title" css={modalTitleStyles}>
              {result.isError ? "Restore failed" : "Restore complete"}
            </h4>
            <div css={modalBodyStyles}>{result.message}</div>
            <div css={modalActionsStyles}>
              <button
                css={modalCloseButtonStyles}
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Paywall() {
  const firstProductFormRef = useRef<HTMLFormElement | null>(null);
  const secondProductFormRef = useRef<HTMLFormElement | null>(null);
  const isInMobileApp = isMobileApp();
  const [isBuyingFirstProduct, setIsBuyingFirstProduct] = useState(false);
  const [isBuyingSecondProduct, setIsBuyingSecondProduct] = useState(false);

  // RevenueCat offerings injected by the mobile app (if in WebView)
  const [rcOfferings, setRcOfferings] = useState<RcOfferingsPayload | null>(
    null
  );

  const [searchParams] = useSearchParams();
  const enablePromotionCodes = searchParams.get("enable_promotion_codes");

  // Consider cache stale after 24 hours
  const OFFERINGS_TTL_MS = 24 * 60 * 60 * 1000;

  const isFresh = (ts?: number) =>
    typeof ts === "number" && Date.now() - ts < OFFERINGS_TTL_MS;

  const selectFreshOfferings = (incoming: any) => {
    if (incoming && isFresh(incoming.fetchedAt)) return incoming;
    return null;
  };

  // On mount: read any cached offerings and subscribe to future injections
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Use localStorage cache if present
    try {
      const cached = window.localStorage.getItem("revcat.offerings.v1");
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const fresh = selectFreshOfferings(parsed);
          if (fresh) {
            console.debug("[Paywall] Using cached RevenueCat offerings", {
              fetchedAt: new Date(fresh.fetchedAt).toISOString(),
            });
            setRcOfferings(fresh);
          } else {
            console.debug(
              "[Paywall] Ignoring stale cached RevenueCat offerings"
            );
          }
        } catch {}
      }
    } catch {}

    // Listen for fresh injections from native
    const handler = (e: any) => {
      if (e && e.detail) {
        const fresh = selectFreshOfferings(e.detail);
        if (fresh) {
          console.debug("[Paywall] Received fresh RevenueCat offerings", {
            fetchedAt: new Date(fresh.fetchedAt).toISOString(),
          });
          setRcOfferings(fresh);
        } else {
          console.debug("[Paywall] Ignored stale RevenueCat offerings event");
        }
      }
    };
    document.addEventListener("revcat:offerings", handler as any);
    return () =>
      document.removeEventListener("revcat:offerings", handler as any);
  }, []);

  // Unified product identifiers (mobile + web metadata)
  const products: PaywallProducts = {
    firstProductBox: {
      // e.g., Credits Pack (one-time)
      purchaseIds: {
        ios: "sharpana_credit_pack_3_99_v1",
        android: "sharpana_tokenpack_v1",
      },
      pricingIds: {
        ios: "sharpana_credit_pack_3_99_v1",
        android: "sharpana_tokenpack_v1",
      },
      productType: "non_subscription",
      productLogicalNameId: "token_pack_30",
    },
    secondProductBox: {
      // e.g., Subscription (recurring)
      purchaseIds: {
        ios: "sharpana_monthly_tier1_6_99_v1",
        android: "sharpana_subscription_v1",
      },
      pricingIds: {
        ios: "sharpana_monthly_tier1_6_99_v1",
        android: "sharpana_subscription_v1:sharpana-subscription-v1",
      },
      productType: "subscription",
      productLogicalNameId: "subscription_plus",
    },
  };

  // On mount (mobile app): ask native layer to preload the products shown in the paywall
  useEffect(() => {
    if (!isInMobileApp) return;
    try {
      const allEntries = Object.values(products).map((p) => ({
        productIds: p.purchaseIds,
        productType: p.productType,
      }));
      preloadPurchaseProducts(allEntries);
    } catch (e) {
      // Best-effort only
      // console.debug("preloadPurchaseProducts error", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve dynamic product info from injected offerings (mobile only)
  const store: Store | undefined = rcOfferings?.store;
  const firstPid = store
    ? products.firstProductBox.pricingIds[store]
    : undefined;
  const secondPid = store
    ? products.secondProductBox.pricingIds[store]
    : undefined;
  const firstProduct = firstPid
    ? rcOfferings?.productsById?.[firstPid]
    : undefined;
  const secondProduct = secondPid
    ? rcOfferings?.productsById?.[secondPid]
    : undefined;
  const firstPriceString: string | undefined = firstProduct?.priceString;
  const firstCurrencyCode: string | undefined = firstProduct?.currencyCode;
  const secondPriceString: string | undefined = secondProduct?.priceString;
  const secondCurrencyCode: string | undefined = secondProduct?.currencyCode;

  // Restore purchases action is handled within <RestorePurchasesAction /> component.

  const handleFirstProductPayment = () => {
    // Prevent duplicate submits on web while processing
    if (!isInMobileApp && isBuyingFirstProduct) return;

    if (isInMobileApp) {
      notifyRequestPayment(
        products.firstProductBox.purchaseIds,
        products.firstProductBox.productType
      );
    } else {
      setIsBuyingFirstProduct(true);
      firstProductFormRef.current?.submit();
    }
  };

  const handleSecondProductPayment = () => {
    // Prevent duplicate submits on web while processing
    if (!isInMobileApp && isBuyingSecondProduct) return;

    if (isInMobileApp) {
      notifyRequestPayment(
        products.secondProductBox.purchaseIds,
        products.secondProductBox.productType
      );
    } else {
      setIsBuyingSecondProduct(true);
      secondProductFormRef.current?.submit();
    }
  };

  return (
    <div css={containerStyles}>
      <div css={headerStyles}>
        {/* <h3>Unlock Turbo for learning</h3> */}
        <h3>
          <span css={glowUnderline}>Boost Your Learning</span>
        </h3>
        <p css={mainDescription}>
          {/* The way we consume information is changing. 
        Don't let old ways hold you back. <br/> */}
          {/* Go beyond with crafted interactive summaries that move at the speed of your curiosity. <br/>
          Get connected to the ideas that matter, we make "I'll check it out later" happen. */}
          For the world of ideas you've been meaning to explore, our{" "}
          {/* <span css={highlight}> interactive summaries</span> connect you */}
          interactive summaries connect you <strong> efficiently </strong>with
          the knowledge you care about—letting you learn at the{" "}
          {/* <span css={glowUnderline}>speed of your curiosity</span>. */}
          speed of your curiosity.
        </p>
        {/* <p>Less noise. More signal. Sharper you.</p> */}
        {/* <p>Hone your focus. Minimize noise. Amplify insights. Sharpen your mind.</p> */}
        <p css={taglineStyles}>
          <span className="minimize">Minimize noise.</span>{" "}
          <span className="amplify">
            {/* Unlock insights <span className="faster">faster</span>.*/}
            Amplify insights.
          </span>
          <br className="mobile-break" />
          <span className="sharpen">Sharpen your mind.</span>
        </p>
        {/* <p>Cut the noise. Amplify the insights. Sharpen your focus.</p> */}
      </div>

      <div css={productsContainerStyles}>
        {/* Credits Pack */}
        <div css={productCardStyles}>
          <div css={productHeaderStyles}>
            <h3>Credits Pack</h3>
            <p className="description">
              Perfect for occasional users who need high-quality interactive
              summaries
            </p>
          </div>

          <div css={priceStyles}>
            <span className="price">
              {isInMobileApp && firstPriceString ? (
                <>
                  {firstPriceString}
                  {firstCurrencyCode ? (
                    <sup className="usd">{firstCurrencyCode}</sup>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="currency">$</span>3.99
                  <sup className="usd">USD</sup>
                </>
              )}
            </span>
          </div>

          <div css={featuresStyles}>
            <ul>
              <li>30 summary credits</li>
              <li>1 credit = 1 AI summary</li>
              {isInMobileApp ? <></> : <li>Credits are good for 90 days</li>}
              <li>Interactive features (Q&A, dig deeper)</li>
              <li>Seamless integration with your YouTube playlists</li>
              <li>Private by default</li>
              <li>No recurring charges — top up anytime</li>
            </ul>
          </div>

          {!isInMobileApp && (
            <form
              ref={firstProductFormRef}
              action={`/api/create-stripe-checkout-session?enable_promotion_codes=${enablePromotionCodes}`}
              method="POST"
              style={{ display: "none" }}
            >
              <input
                type="hidden"
                name="product_name"
                value={products.firstProductBox.productLogicalNameId}
              />
            </form>
          )}

          <button
            css={buttonStyles}
            onClick={handleFirstProductPayment}
            disabled={!isInMobileApp && isBuyingFirstProduct}
            aria-busy={isBuyingFirstProduct}
          >
            {isBuyingFirstProduct ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span css={buttonLoading} aria-hidden="true" />
                Processing…
              </span>
            ) : (
              "Buy Credits"
            )}
          </button>
        </div>

        {/* Sharpana Plus Subscription */}
        <div css={productCardStyles}>
          {/* <div css={popularBadgeStyles}>Most Popular</div> */}

          <div css={productHeaderStyles}>
            <h3>Sharpana Plus</h3>
            <p className="description">
              For professionals who need to stay sharp and boost their access to
              information
            </p>
          </div>

          <div css={priceStylesShifted}>
            <span className="price">
              {isInMobileApp && secondPriceString ? (
                <>
                  {secondPriceString}
                  {secondCurrencyCode ? (
                    <sup className="usd">{secondCurrencyCode}</sup>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="currency">$</span>6.99
                  <sup className="usd">USD</sup>
                </>
              )}
            </span>
            <span className="period">/month</span>
          </div>

          <div css={featuresStyles}>
            <ul>
              <li>Unlimited AI summaries*</li>
              <li>Interactive features (Q&A, dig deeper)</li>
              <li>Seamless integration with your YouTube playlists</li>
              <li>Priority support and feature requests</li>
              <li>Private by default</li>
              <li>Cancel anytime</li>
              <li className="heart">Support Sharpana's growth</li>
            </ul>
          </div>

          {!isInMobileApp && (
            <form
              ref={secondProductFormRef}
              action={`/api/create-stripe-checkout-session?enable_promotion_codes=${enablePromotionCodes}`}
              method="POST"
              style={{ display: "none" }}
            >
              <input
                type="hidden"
                name="product_name"
                value={products.secondProductBox.productLogicalNameId}
              />
            </form>
          )}

          <button
            css={buttonStyles}
            onClick={handleSecondProductPayment}
            disabled={!isInMobileApp && isBuyingSecondProduct}
            aria-busy={isBuyingSecondProduct}
          >
            {isBuyingSecondProduct ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span css={buttonLoading} aria-hidden="true" />
                Processing…
              </span>
            ) : (
              "Subscribe Now"
            )}
          </button>
        </div>
      </div>
      <div css={bottomRowNotes}>
        <div css={productHeaderStyles}>
          <p className="description">
            Note: Our free plan lets you try basic features and includes 5
            credits.
          </p>

          <p className="description">
            *Unlimited does not mean unrestricted. Automated scraping is not
            allowed. The term unlimited applies in the context of real human
            users generating summaries for learning, research, or personal use.
          </p>

          <RestorePurchasesAction />
        </div>
        <div css={legalLinksStyles}>
          <a
            href="https://terms.mrble.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
          <span className="dot">•</span>
          <a
            href="https://policy.mrble.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
